/**
 * FI Income Calendar — types and display helpers for GET /get_fi_income_calendar
 * (backend strategy/fi_income_calendar.py). DISPLAY ONLY: nothing here feeds the FI engine.
 *
 * Every estimate carries its source. A projected ex-date is labelled as projected; a holding with
 * no data shows "—" and a flag, never a $0 estimate.
 */

export type ExDateSource = "IBKR" | "MORNINGSTAR" | "PROJECTED" | null;
export type AmountSource = "IBKR" | "MORNINGSTAR" | "MORNINGSTAR_MEDIAN_3" | null;

export type IncomeRow = {
  ticker: string;
  shares: number;
  market_value: number;
  next_ex_date: string | null;
  next_ex_date_source: ExDateSource;
  days_to_ex: number | null;
  est_per_share: number | null;
  est_per_share_source: AmountSource;
  est_payment: number | null;
  last_ex_date: string | null;
  last_per_share: number | null;
  ttm_per_share: number | null;
  est_annual_income: number | null;
  yield_12m_pct: number | null;
  frequency: string | null;
  flags: string[];
};

export type IncomeCalendar = {
  rows: IncomeRow[];
  totals: {
    est_next_30d: number;
    est_next_30d_count: number;
    est_annual_income: number;
    holdings_count: number;
    holdings_without_estimate: number;
  };
  morningstar: {
    available: boolean;
    retrieved_at_utc: string | null;
    age_days: number | null;
    stale: boolean;
    stale_after_days: number;
    error: string | null;
  };
  ibkr: { available: boolean; error: string | null; declared_count: number };
  as_of_date: string;
  notes: Record<string, string>;
  holdings_updated_utc?: string | null;
};

export const exDateLabel = (r: IncomeRow): { text: string; tag: string | null; title: string } => {
  if (!r.next_ex_date) return { text: "—", tag: null, title: "No declared ex-date and not enough history to project one." };
  if (r.next_ex_date_source === "IBKR")
    return { text: r.next_ex_date, tag: "declared", title: "Ex-date declared by the fund (IBKR)." };
  if (r.next_ex_date_source === "MORNINGSTAR")
    return { text: r.next_ex_date, tag: "declared", title: "Ex-date and amount declared by the fund (Morningstar)." };
  return {
    text: r.next_ex_date,
    tag: "projected",
    title: "Projected from the fund's own distribution history (last ex-date + typical gap). Not yet declared; may move a few days.",
  };
};

export const amountSourceLabel = (s: AmountSource): string =>
  s === "IBKR"
    ? "IBKR's amount for the declared dividend"
    : s === "MORNINGSTAR"
    ? "Declared amount (Morningstar)"
    : s === "MORNINGSTAR_MEDIAN_3"
    ? "Median of the last three distributions (Morningstar)"
    : "No estimate";

export const daysLabel = (d: number | null): string =>
  d == null ? "" : d === 0 ? "today" : d === 1 ? "in 1 day" : `in ${d} days`;

export type Freshness = { tone: "positive" | "warning" | "danger"; text: string };

export const morningstarFreshness = (m: IncomeCalendar["morningstar"]): Freshness => {
  if (!m.available) return { tone: "danger", text: `Morningstar data unavailable${m.error ? ` — ${m.error}` : ""}` };
  const when = m.retrieved_at_utc ? m.retrieved_at_utc.slice(0, 10) : "unknown date";
  const age = m.age_days == null ? "" : m.age_days === 0 ? " (today)" : ` (${m.age_days}d ago)`;
  if (m.stale)
    return { tone: "warning", text: `Morningstar data as of ${when}${age} — overdue (refresh runs the 2nd and 23rd)` };
  return { tone: "positive", text: `Morningstar data as of ${when}${age}` };
};

export const fmtMoney = (v: number | null | undefined, digits = 0): string =>
  v == null || Number.isNaN(v)
    ? "—"
    : v.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: digits, maximumFractionDigits: digits });

export const fmtPerShare = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v) ? "—" : `$${v.toFixed(4)}`;
